import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Drawer, Input, Modal, Space, Table, Tag, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import {
  FaBuilding,
  FaCheckCircle,
  FaClock,
  FaCrown,
  FaExclamationTriangle,
  FaMoneyBill,
  FaPen,
  FaPlus,
  FaSearch,
  FaTimesCircle,
  FaTrash,
  FaUserTie,
} from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import {
  AbonnementType,
  AssetManagerType,
  PackType,
} from '../../../../redux/userStore/reducer';
import { ORANGE, PRIMARY, YELLOW } from '../../../../shared/colors';
import { ExportFormatButton } from '../../../../shared/ExportFormatButton';
import { getExportMetadata } from '../../../../shared/exportFiles';
import {
  deleteAssetManager,
  getAssetManagers,
  getPack,
  listAbonnement,
} from '../../../dashboard/network';
import { Layout } from '../../../shared/Layout';
import { AbonnementForm } from '../components/AbonnementForm';
import { AssetForm } from '../components/AssetForm';
import { AssetUpdateForm } from '../components/AssetUpdateForm';
import { exportMoranjeClientsData } from '../components/exportDataExcel';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .bread-crum span {
    color: var(--mt-text);
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-text);
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: ${YELLOW} !important;
      cursor: pointer;
      color: var(--mt-text);
    }
  }

  .content {
    padding: 12px;
    width: 100%;
    background: transparent;
    color: var(--mt-text);
  }

  .clients-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    margin-bottom: 12px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.12), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .clients-hero h1 {
    margin: 8px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
  }

  .clients-hero p {
    max-width: 720px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
  }

  .hero-badge {
    min-width: 180px;
    border: 1px solid rgba(255, 176, 0, 0.28);
    border-radius: 8px;
    padding: 12px;
    color: #fff7e8;
    background: rgba(255, 255, 255, 0.06);
  }

  .hero-badge span {
    display: block;
    color: rgba(255, 247, 232, 0.62);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .hero-badge strong {
    display: block;
    margin-top: 6px;
    color: ${YELLOW};
    font-family: Poppins-B;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }

  .kpi-card {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 14px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-icon {
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    border-radius: 8px;
    color: ${YELLOW};
    background: rgba(255, 176, 0, 0.12);
  }

  .kpi-card span {
    display: block;
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .kpi-card strong {
    display: block;
    margin-top: 3px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 20px;
  }

  .clients-panel {
    overflow: hidden;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    border-bottom: 1px solid var(--mt-border);
  }

  .panel-title span {
    display: block;
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .panel-title h2 {
    margin: 4px 0 0;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 22px;
  }

  .page-toolbar .ant-input {
    width: min(100%, 340px);
    height: 42px;
    border-color: var(--mt-border);
    border-radius: 8px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .page-toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }

  .filter-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 18px 16px;
  }

  .filter-pill {
    height: 34px;
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    background: var(--mt-surface);
    color: var(--mt-muted);
    font-family: Poppins-B;
  }

  .filter-pill.active {
    border-color: rgba(255, 176, 0, 0.42);
    background: rgba(255, 176, 0, 0.12);
    color: ${YELLOW};
  }

  .primary-action {
    height: 42px;
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    color: #07141c;
    font-family: Poppins-B;
  }

  .secondary-action {
    height: 42px;
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    background: var(--mt-surface);
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .client-name {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .client-avatar {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    color: #07141c;
    background: ${YELLOW};
    font-family: Poppins-B;
    font-size: 12px;
  }

  .client-name strong {
    display: block;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .client-name span {
    color: var(--mt-muted);
    font-size: 12px;
  }

  .muted-cell {
    color: var(--mt-muted);
  }

  .status-tag {
    border-radius: 999px;
    padding: 3px 10px;
    font-family: Poppins-B;
    font-size: 11px;
  }

  .subscription-pack strong {
    display: block;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .subscription-pack span {
    color: var(--mt-muted);
    font-size: 12px;
  }

  .action-button {
    display: inline-grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid var(--mt-border);
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .action-button:hover {
    border-color: ${YELLOW};
    color: ${YELLOW};
  }

  .table-shell {
    padding: 0 18px 18px;
  }

  .ant-table {
    background: transparent;
    color: var(--mt-text);
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--mt-border);
    color: var(--mt-text);
  }

  .delete-modal .ant-modal-content {
    border-radius: 8px;
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .delete-modal .ant-modal-header {
    background: transparent;
    border-bottom: 1px solid var(--mt-border);
  }

  .delete-modal .ant-modal-title {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .delete-warning {
    display: flex;
    gap: 14px;
    border: 1px solid rgba(255, 59, 48, 0.24);
    border-radius: 8px;
    padding: 14px;
    background: rgba(255, 59, 48, 0.07);
  }

  .delete-warning svg {
    flex: 0 0 auto;
    margin-top: 3px;
    color: #ff3b30;
  }

  .delete-warning strong {
    display: block;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 16px;
  }

  .delete-warning p {
    margin: 6px 0 0;
    color: var(--mt-muted);
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    width: 100%;
  }

  .modal-cancel,
  .modal-delete {
    min-width: 120px;
    height: 40px;
    border-radius: 999px;
    font-family: Poppins-B;
  }

  .modal-cancel {
    border: 1px solid var(--mt-border);
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .modal-delete {
    border: 0;
    background: #ff3b30;
    color: white;
  }

  @media (max-width: 760px) {
    .content {
      padding: 18px 12px;
    }

    .clients-hero,
    .panel-header,
    .page-toolbar {
      flex-direction: column;
      align-items: stretch;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }

    .filter-pills {
      padding-inline: 14px;
    }
  }
`;

const adminDrawerStyles = css`
  .mt-admin-drawer .ant-drawer-content {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .mt-admin-drawer .ant-drawer-header {
    border-bottom: 1px solid var(--mt-border);
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.12), transparent 38%),
      ${PRIMARY};
  }

  .mt-admin-drawer .ant-drawer-title,
  .mt-admin-drawer .ant-drawer-close {
    color: #fff7e8;
    font-family: Poppins-B;
  }

  .mt-admin-drawer .ant-drawer-body {
    background: var(--mt-bg);
    color: var(--mt-text);
    padding: 18px;
  }

  .mt-admin-drawer .ant-form-item-label label {
    color: var(--mt-muted);
    font-family: Poppins-B;
    font-size: 12px;
    text-transform: uppercase;
  }

  .mt-admin-drawer .ant-input,
  .mt-admin-drawer .ant-select-selector {
    min-height: 42px;
    border-color: var(--mt-border) !important;
    border-radius: 8px !important;
    background: var(--mt-surface) !important;
    color: var(--mt-text) !important;
  }

  .mt-admin-drawer .ant-select-selection-placeholder,
  .mt-admin-drawer .ant-input::placeholder {
    color: var(--mt-muted) !important;
  }

  .mt-admin-drawer .ant-table {
    background: transparent;
    color: var(--mt-text);
  }

  .mt-admin-drawer .drawer-intro {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 14px;
    margin-bottom: 14px;
    background: var(--mt-card);
  }

  .mt-admin-drawer .drawer-intro span {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .mt-admin-drawer .drawer-intro h2 {
    margin: 4px 0;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .mt-admin-drawer .drawer-intro p {
    margin: 0;
    color: var(--mt-muted);
  }

  .mt-admin-drawer .ant-btn-primary,
  .mt-admin-drawer .butt {
    min-width: 180px;
    height: 42px;
    border: 0 !important;
    border-radius: 999px !important;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW}) !important;
    color: #07141c !important;
    font-family: Poppins-B;
  }

  .mt-admin-drawer .danger-action {
    min-width: 160px;
    height: 40px;
    border: 1px solid rgba(255, 59, 48, 0.35);
    border-radius: 999px;
    background: rgba(255, 59, 48, 0.08);
    color: #ff3b30;
    font-family: Poppins-B;
  }
`;

const KpiCard = ({
  icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string | number;
}) => (
  <div className='kpi-card'>
    <div className='kpi-icon'>{icon}</div>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  </div>
);

type ClientFilter = 'all' | 'active' | 'expired' | 'without';

export const MoranjeAdmin = () => {
  const COLUMNS = [
    {
      title: 'NOM CLIENT',
      render: (cell: any, row: AssetManagerType) => (
        <div className='client-name'>
          <div className='client-avatar'>{getInitials(row.nom)}</div>
          <div>
            <strong>{row.nom || '--'}</strong>
            <span>Client MoranjETrade</span>
          </div>
        </div>
      ),
      sorter: (a: AssetManagerType, b: AssetManagerType) =>
        safeText(a.nom).localeCompare(safeText(b.nom)),
    },
    {
      title: 'EMAIL',
      render: (cell: any, row: AssetManagerType) => (
        <span className='muted-cell'>{row.email || '--'}</span>
      ),
      sorter: (a: AssetManagerType, b: AssetManagerType) =>
        safeText(a.email).localeCompare(safeText(b.email)),
    },
    {
      title: 'ADRESSE',
      render: (cell: any, row: AssetManagerType) => (
        <span className='muted-cell'>{row.adresse || '--'}</span>
      ),
      sorter: (a: AssetManagerType, b: AssetManagerType) =>
        safeText(a.adresse).localeCompare(safeText(b.adresse)),
    },
    {
      title: 'CONTACT',
      render: (cell: any, row: AssetManagerType) => row.contact || '--',
      sorter: (a: AssetManagerType, b: AssetManagerType) =>
        safeText(a.contact).localeCompare(safeText(b.contact)),
    },
    {
      title: 'ABONNEMENT',
      render: (cell: any, row: AssetManagerType) => {
        const active = isSubscriptionActive(row.fin_abonnement);

        return active ? (
          <Tag className='status-tag' color='success' icon={<FaCheckCircle />}>
            En cours
          </Tag>
        ) : (
          <Tag className='status-tag' color='error' icon={<FaTimesCircle />}>
            Expiré
          </Tag>
        );
      },
      flex: 2,
    },
    {
      title: 'FIN ABONNEMENT',
      render: (cell: any, row: AssetManagerType) => formatDate(row.fin_abonnement),
    },
    {
      title: 'Action',
      render: (cell: any, row: AssetManagerType) => (
        <Space>
          <Tooltip title='Modifier le client'>
            <Button
              className='action-button'
              icon={<FaPen />}
              onClick={() => {
                setSelectedAsset(row);
                setShowDrawer1(true);
              }}
            />
          </Tooltip>
          <Tooltip title='Gérer l’abonnement'>
            <Button
              className='action-button'
              icon={<FaMoneyBill />}
              onClick={() => {
                setSelectedAsset(row);
                setShowDrawer2(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const COLUMNS_AB = [
    {
      title: 'NOM PACK',
      render: (cell: any, row: AbonnementType) => (
        <div className='subscription-pack'>
          <strong>{row.pack?.nom || '--'}</strong>
          <span>Pack souscrit</span>
        </div>
      ),
      sorter: (a: AbonnementType, b: AbonnementType) =>
        safeText(a.pack?.nom).localeCompare(safeText(b.pack?.nom)),
    },
    {
      title: 'Montant',
      render: (cell: any, row: AbonnementType) =>
        `${Number(row.pack?.amount || 0).toLocaleString('fr-FR')} XAF`,
    },
    {
      title: 'Date souscription',
      render: (cell: any, row: AbonnementType) => formatDate(row.created_at),
    },
    {
      title: 'Date fin',
      render: (cell: any, row: AbonnementType) => formatDate(row.fin_abonnement),
    },
    {
      title: 'Statut',
      render: (cell: any, row: AbonnementType) =>
        isSubscriptionActive(row.fin_abonnement) ? (
          <Tag className='status-tag' color='success' icon={<FaCheckCircle />}>
            Actif
          </Tag>
        ) : (
          <Tag className='status-tag' color='error' icon={<FaTimesCircle />}>
            Expiré
          </Tag>
        ),
    },
  ];
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDrawer1, setShowDrawer1] = useState(false);
  const [showDrawer2, setShowDrawer2] = useState(false);
  const [assetManager, setAssetManager] = useState<AssetManagerType[]>([]);
  const [filterClient, setFilterClient] = useState<AssetManagerType[]>([]);
  const [pack, setPack] = useState<PackType[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<AssetManagerType>({
    id: '',
    nom: '',
    adresse: '',
    email: '',
    contact: '',
    created_at: '',
    updated_at: '',
    count: '',
    achat: 0,
    vente: 0,
    fin_abonnement: '',
    abonnement: [],
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<ClientFilter>('all');

  const applyClientFilters = (
    clients: AssetManagerType[],
    search = searchTerm,
    filter = activeFilter,
  ) => {
    const normalizedSearch = search.toLowerCase();

    return clients.filter((client) => {
      const matchesSearch = [client.nom, client.adresse, client.email, client.contact].some(
        (value) => safeText(value).toLowerCase().includes(normalizedSearch),
      );
      const isActive = isSubscriptionActive(client.fin_abonnement);
      const hasSubscription = Boolean(client.abonnement?.length);
      const matchesFilter =
        filter === 'all' ||
        (filter === 'active' && isActive) ||
        (filter === 'expired' && !isActive) ||
        (filter === 'without' && !hasSubscription);

      return matchesSearch && matchesFilter;
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = () => {
    setIsLoading(true);
    getAssetManagers().then(({ data: filterClient }) => {
      filterClient.map((item: AssetManagerType) => {
        item.abonnement = [];
        listAbonnement(connectedUser.id, item.id).then(({ data }) => {
          item.abonnement = data;
        });
        return item;
      });
      setAssetManager(filterClient);
      setFilterClient(applyClientFilters(filterClient));
      console.log(filterClient);
      setIsLoading(false);
    });
    getPack().then(({ data }) => {
      setPack(data);
    });
  };
  const deleteAsset = () => {
    setIsLoading(true);
    if (selectedAsset) {
      deleteAssetManager(connectedUser.id, selectedAsset.id).then(() => {
        setIsLoading(false);
        setShowDeleteModal(false);
        fetchData();
      });
    }
  };
  const activeClients = assetManager.filter((client) =>
    isSubscriptionActive(client.fin_abonnement),
  ).length;
  const expiredClients = Math.max(assetManager.length - activeClients, 0);
  const totalSubscriptions = assetManager.reduce(
    (total, client) => total + (client.abonnement?.length || 0),
    0,
  );
  const withoutSubscription = assetManager.filter(
    (client) => !client.abonnement?.length,
  ).length;

  const handleFilterChange = (filter: ClientFilter) => {
    setActiveFilter(filter);
    setFilterClient(applyClientFilters(assetManager, searchTerm, filter));
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setFilterClient(applyClientFilters(assetManager, value, activeFilter));
  };

  return (
    <Layout>
      <Global styles={adminDrawerStyles} />
      <Container>
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <section className='clients-hero'>
            <div>
              <span className='eyebrow'>
                <FaBuilding />
                Administration
              </span>
              <h1>Clients MoranjETrade</h1>
              <p>
                Pilotez les sociétés clientes, leurs contacts opérationnels et
                leurs abonnements depuis un registre unique.
              </p>
            </div>
            <div className='hero-badge'>
              <span>Registre actif</span>
              <strong>{assetManager.length} clients</strong>
            </div>
          </section>

          <section className='kpi-grid'>
            <KpiCard icon={<FaUserTie />} label='Clients enregistrés' value={assetManager.length} />
            <KpiCard icon={<FaCheckCircle />} label='Abonnements actifs' value={activeClients} />
            <KpiCard icon={<FaClock />} label='À renouveler' value={expiredClients} />
            <KpiCard icon={<FaCrown />} label='Souscriptions' value={totalSubscriptions} />
          </section>

          <section className='clients-panel'>
            <div className='panel-header'>
              <div className='panel-title'>
                <span>Client registry</span>
                <h2>Liste des clients MoranjETrade</h2>
              </div>
              <div className='page-toolbar'>
                <Input
                  prefix={<FaSearch />}
                  placeholder='Rechercher nom, email, adresse...'
                  onChange={(e) => {
                    handleSearch(e.target.value);
                  }}
                />

                <ExportFormatButton
                  className='secondary-action'
                  disabled={!filterClient.length}
                  label='Exporter'
                  onExport={(format) =>
                    exportMoranjeClientsData(
                      filterClient,
                      format,
                      getExportMetadata(connectedUser),
                    )
                  }
                />

                <Button
                  icon={<FaPlus style={{ marginBottom: -2, marginRight: 5 }} />}
                  className='primary-action'
                  onClick={() => setShowDrawer(true)}
                >
                  Nouveau client
                </Button>
              </div>
            </div>
            <div className='filter-pills'>
              <Button
                className={`filter-pill ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => handleFilterChange('all')}
              >
                Tous · {assetManager.length}
              </Button>
              <Button
                className={`filter-pill ${activeFilter === 'active' ? 'active' : ''}`}
                onClick={() => handleFilterChange('active')}
              >
                Actifs · {activeClients}
              </Button>
              <Button
                className={`filter-pill ${activeFilter === 'expired' ? 'active' : ''}`}
                onClick={() => handleFilterChange('expired')}
              >
                Expirés · {expiredClients}
              </Button>
              <Button
                className={`filter-pill ${activeFilter === 'without' ? 'active' : ''}`}
                onClick={() => handleFilterChange('without')}
              >
                Sans abonnement · {withoutSubscription}
              </Button>
            </div>
            <div className='table-shell'>
            <Table
              columns={COLUMNS}
              dataSource={filterClient}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              size='small'
              loading={isLoading}
              scroll={{ x: 980 }}
              pagination={false}
              rowKey='id'
              locale={{
                emptyText: 'Aucun client ne correspond à vos filtres.',
              }}
              onRow={(row, rowIndex) => {
                return {
                  onClick: (event) => {}, // click row
                  onDoubleClick: (event) => {}, // double click row
                  onContextMenu: (event) => {}, // right button click row
                  onMouseEnter: (event) => {}, // mouse enter row
                  onMouseLeave: (event) => {}, // mouse leave row
                };
              }}
            />
            </div>
          </section>
        </div>
        <Drawer
          title='Ajouter un client MoranjEtrade'
          className='mt-admin-drawer'
          placement='right'
          width='min(100vw, 520px)'
          onClose={() => setShowDrawer(false)}
          open={showDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <AssetForm
            setShowDrawer={setShowDrawer}
            getNewAsset={(part) => {
              const nextClients = [...assetManager, part];
              setAssetManager(nextClients);
              setFilterClient(applyClientFilters(nextClients));
            }}
          />
        </Drawer>
        <Drawer
          title="Abonnement d'un client MoranjEtrade"
          className='mt-admin-drawer'
          placement='right'
          width='min(100vw, 680px)'
          onClose={() => setShowDrawer2(false)}
          open={showDrawer2}
          closeIcon={<MdClose size={20} />}
        >
          <div className='drawer-intro'>
            <span>Gestion abonnement</span>
            <h2>{selectedAsset.nom}</h2>
            <p>Consultez les souscriptions existantes et ajoutez un nouveau pack.</p>
          </div>
          <Table
            columns={COLUMNS_AB}
            dataSource={selectedAsset.abonnement}
            rowClassName='style-row'
            size='small'
            scroll={{ x: 640 }}
            pagination={false}
            rowKey='id'
            locale={{
              emptyText: 'Aucun abonnement souscrit pour ce client.',
            }}
            onRow={(row, rowIndex) => {
              return {
                onClick: (event) => {}, // click row
                onDoubleClick: (event) => {}, // double click row
                onContextMenu: (event) => {}, // right button click row
                onMouseEnter: (event) => {}, // mouse enter row
                onMouseLeave: (event) => {}, // mouse leave row
              };
            }}
          />
          <AbonnementForm
            setShowDrawer={setShowDrawer2}
            pack={pack}
            settheasset={selectedAsset}
            getNewAsset={(part) => {
              setAssetManager(part);
              setFilterClient(applyClientFilters(part));
            }}
          />
        </Drawer>
        <Drawer
          title='Modifier un client'
          className='mt-admin-drawer'
          placement='right'
          width='min(100vw, 520px)'
          onClose={() => setShowDrawer1(false)}
          open={showDrawer1}
          closeIcon={<MdClose size={20} />}
        >
          <AssetUpdateForm
            setShowDrawer={setShowDrawer1}
            settheasset={selectedAsset}
            getNewAsset={(client) => {
              setAssetManager(client);
              setFilterClient(applyClientFilters(client));
            }}
          />
          <Button
            icon={<FaTrash style={{ marginBottom: -2, marginRight: 5 }} />}
            className='danger-action'
            loading={isLoading}
            onClick={() => {
              setShowDeleteModal(true);
              setShowDrawer1(false);
            }}
          >
            Supprimer
          </Button>
        </Drawer>
        <Modal
          open={showDeleteModal}
          title='Supprimer le client'
          className='delete-modal'
          onCancel={() => setShowDeleteModal(false)}
          footer={[
            <div className='modal-actions' key='delete-actions'>
              <Button
                className='modal-cancel'
                onClick={() => setShowDeleteModal(false)}
              >
                Annuler
              </Button>
              <Button
                className='modal-delete'
                loading={isLoading}
                onClick={deleteAsset}
              >
                Supprimer
              </Button>
            </div>,
          ]}
        >
          <div className='delete-warning'>
            <FaExclamationTriangle />
            <div>
              <strong>{selectedAsset.nom || 'Client sélectionné'}</strong>
              <p>
                Cette action supprimera le client MoranjETrade sélectionné et
                peut impacter ses abonnements associés. Confirmez uniquement si
                le dossier doit réellement être retiré du registre.
              </p>
            </div>
          </div>
        </Modal>
      </Container>
    </Layout>
  );
};

const safeText = (value?: string | number | null) => `${value ?? ''}`;

const isSubscriptionActive = (date?: string) => {
  const timestamp = Date.parse(date || '');

  return !Number.isNaN(timestamp) && timestamp >= Date.now();
};

const formatDate = (date?: string) => {
  const timestamp = Date.parse(date || '');

  if (Number.isNaN(timestamp)) {
    return '--';
  }

  return new Intl.DateTimeFormat('fr-FR').format(new Date(timestamp));
};

const getInitials = (name?: string) =>
  safeText(name)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'ME';
